import React from 'react';
import PropTypes from 'prop-types';
import { cn } from '~/lib/utils';

// Shared class string for nav row links and buttons (active/inactive state).
// Used inline in MenuItem (as <a>) and MenuLinkBtn (as <button>).
export const navRowClasses = (isActive) => cn(
	'flex items-center flex-nowrap',
	'transition-colors duration-fast',
	'border-0 border-l-2 border-solid',
	'bg-transparent p-0',
	'text-sm font-bold',
	'focus:outline-none',
	isActive
		? 'border-primary text-white'
		: 'border-transparent text-white text-opacity-30',
	'hover:bg-nav-hover hover:text-white'
);

// Plain ul — flex column list
export function Menu({ children, className }) {
	return (
		<ul className={cn('flex flex-col flex-nowrap m-0 p-0 list-none', className)}>
			{children}
		</ul>
	);
}
Menu.propTypes = { children: PropTypes.node, className: PropTypes.string };

// Shared label span — hidden in collapsed nav, shown when expanded
export function MenuLabel({ children, className }) {
	return (
		<span className={cn('-translate-x-1 whitespace-nowrap', className)}>
			{children}
		</span>
	);
}
MenuLabel.propTypes = { children: PropTypes.node, className: PropTypes.string };

// Nav list item — handles active border + tooltip visibility via data attrs
export function MenuItem({ children, className, isActive, ...props }) {
	return (
		<li className={cn('relative outline-none', className)} {...props}>
			<a className={navRowClasses(isActive)}>
				{children}
			</a>
		</li>
	);
}
MenuItem.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	isActive: PropTypes.bool
};

// Button variant of nav row (used by avatar-menu, notifications, theme-switcher)
export function MenuLinkBtn({ children, className, isActive, ...props }) {
	return (
		<button
			type="button"
			className={cn(navRowClasses(isActive), 'w-full text-left', className)}
			{...props}
		>
			{children}
		</button>
	);
}
MenuLinkBtn.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	isActive: PropTypes.bool
};

// Outer container — controls expanded width and label/tooltip visibility
export function Container({ children, className, isMaximized, ...props }) {
	return (
		<div
			className={cn(
				'flex flex-col justify-between h-full',
				isMaximized ? 'w-48' : 'w-auto',
				// When collapsed, show tooltips; when maximized, hide them
				isMaximized ? 'nav-maximized' : 'nav-collapsed',
				className
			)}
			{...props}
		>
			{children}
		</div>
	);
}
Container.propTypes = {
	children: PropTypes.node,
	className: PropTypes.string,
	isMaximized: PropTypes.bool
};
